import type { AlreadyRead } from './valueObjects/alreadyRead';
import type { Category, CategoryType } from './valueObjects/category';
import type { ContentS3Path } from './valueObjects/contentS3Path';
import type { CreateDatetime } from './valueObjects/createDatetime';
import type { FromId } from './valueObjects/fromId';
import type { SendDatetime } from './valueObjects/sendDatetime';
import type { Title } from './valueObjects/title';
import type { ToId } from './valueObjects/toId';

import { EntityPub } from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 送信済みお知らせプロパティ
 */
export type SentNewsPropsPvt = {
  /** 送信日時 */
  sendDatetime: CreateDatetime,
  /** タイトル */
  title: Title,
  /** カテゴリ */
  category: Category,
  /** S3パス */
  s3Path: ContentS3Path,
  /** 送信元ID */
  fromId: FromId,
  /** 送信先ID */
  toId: ToId,
}

/**
 * 送信済みお知らせ公開プロパティ
 */
export type SentNewsPropsPub = {
  /** 既読 */
  alreadyRead: AlreadyRead,
}

/**
 * 送信済みお知らせJSON非公開
 */
export type SentNewsJsonPvt = {
  /** 送信日時 */
  sendDatetime: string,
  /** タイトル */
  title: string,
  /** カテゴリ */
  category: CategoryType,
  /** S3パス */
  s3Path: string,
  /** 送信元ID */
  fromId: string,
  /** 送信先ID */
  toId: string,
}

/**
 * 送信済みお知らせJSON公開
 */
export type SentNewsJsonPub = {
  /** 既読 */
  alreadyRead: boolean,
}

/**
 * 送信済みお知らせ
 */
export class SentNewsEntity extends EntityPub<
  SentNewsPropsPvt,
  SentNewsJsonPvt,
  SentNewsPropsPub,
  SentNewsJsonPub
> {
  /** 削除タイプ */
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  /** 削除キー */
  get deleteKey(): string {
    return this.getFutureYear(this.sendDatetime.value, 2).toJSON();
  }

  toJsonData(): SentNewsJsonPvt {
    return {
      sendDatetime: this.sendDatetime.value.toJSON(),
      title: this.title.value,
      category: this.category.value,
      s3Path: this.s3Path.value,
      fromId: this.fromId.value,
      toId: this.toId.value,
    };
  }

  toJsonPubData(): SentNewsJsonPub {
    return {
      alreadyRead: this.alreadyRead.value,
    };
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoAppNewsSentNewsEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    return `${this.sendDatetime.toStringDateTime()}#${this.fromId.value}#${this.toId.value}`;
  }

  /**
   * サブキー
   */
  get subKey() {
    return `${this.fromId.value}#${this.toId.value}#${this.sendDatetime.toStringDateTime()}`;
  }

  /**
   * S3ファイル名
   */
  get s3Filename(): string {
    return 'd.json.gz';
  }

  /**
   * S3パス
   */
  get s3Path(): ContentS3Path {
    return this._value.s3Path;
  }

  /**
   * 送信日時
   */
  get sendDatetime(): SendDatetime {
    return this._value.sendDatetime;
  }

  /**
   * 送信日時
   */
  set sendDatetime(sendDatetime: SendDatetime) {
    this._value.sendDatetime = sendDatetime;
  }

  /**
   * タイトル
   */
  get title(): Title {
    return this._value.title;
  }

  /**
   * タイトル
   */
  set title(title: Title) {
    this._value.title = title;
  }

  /**
   * カテゴリ
   */
  get category(): Category {
    return this._value.category;
  }

  /**
   * カテゴリ
   */
  set category(category: Category) {
    this._value.category = category;
  }

  /**
   * 送信元ID
   */
  get fromId(): FromId {
    return this._value.fromId;
  }

  /**
   * 送信元ID
   */
  set fromId(fromId: FromId) {
    this._value.fromId = fromId;
  }

  /**
   * 送信先ID
   */
  get toId(): ToId {
    return this._value.toId;
  }

  /**
   * 送信先ID
   */
  set toId(toId: ToId) {
    this._value.toId = toId;
  }

  /**
   * 既読
   */
  get alreadyRead(): AlreadyRead {
    return this._valuePubData.data.alreadyRead;
  }

  /**
   * 既読をセットする
   * @param alreadyRead 既読
   */
  setAlreadyRead(alreadyRead: AlreadyRead) {
    this._valuePubData.data.alreadyRead = alreadyRead;
  }
}
